import React, { useState, useEffect, useCallback } from 'react';
import {
  Copy, Plus, Edit, Trash2, ChevronRight, ChevronDown, Loader2, Globe,
  Building2, Save, X, GripVertical,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { useAppTranslation } from '@/contexts/TranslationContext';
import type { OnboardingTemplate, TemplateDetail, OnboardingPhase, OnboardingMilestone, OnboardingTaskTemplate } from '../../../api/onboardingApi';
import * as api from '../../../api/onboardingApi';

const ACTION_TYPES = [
  { value: 'manual', label: 'Manual' },
  { value: 'link', label: 'Link (page route)' },
  { value: 'wizard', label: 'Wizard' },
  { value: 'form', label: 'Form' },
  { value: 'api_check', label: 'API Check' },
];

const DEFAULT_ROLES = ['csm', 'org_admin', 'implementation', 'staff', 'supervisor', 'program_manager'];

const SCOPE_OPTIONS = [
  { value: 'all', label: 'All Templates', desc: 'Platform starters + your org templates' },
  { value: 'platform', label: 'Platform Starters', desc: 'Pre-built templates you can clone' },
  { value: 'own', label: 'My Org', desc: 'Templates owned by your organization' },
] as const;

export default function TemplateLibrary() {
  const { t } = useAppTranslation();
  const [templates, setTemplates] = useState<OnboardingTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<TemplateDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTmpl, setNewTmpl] = useState({ name: '', description: '', customer_type: '' });
  const [scope, setScope] = useState<'all' | 'platform' | 'own'>('all');
  const [expandedPhases, setExpandedPhases] = useState<Set<number>>(new Set());

  // Editing states
  const [showAddPhase, setShowAddPhase] = useState(false);
  const [phaseForm, setPhaseForm] = useState({ name: '', target_duration_days: '', description: '' });
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [milestonePhaseId, setMilestonePhaseId] = useState<number | null>(null);
  const [milestoneForm, setMilestoneForm] = useState({ name: '', exit_criteria: '', description: '' });
  const [showAddTask, setShowAddTask] = useState(false);
  const [taskMilestoneId, setTaskMilestoneId] = useState<number | null>(null);
  const [taskForm, setTaskForm] = useState({ name: '', description: '', action_type: 'manual', action_target: '', default_owner_role: '', estimated_hours: '' });
  const [saving, setSaving] = useState(false);

  // Inline editing for template name
  const [editingTmplName, setEditingTmplName] = useState(false);
  const [tmplNameValue, setTmplNameValue] = useState('');
  const saveTmplName = async () => {
    if (!tmplNameValue.trim() || !selectedId) { setEditingTmplName(false); return; }
    try {
      await api.updateTemplate(selectedId, { name: tmplNameValue.trim() });
      setEditingTmplName(false); fetchDetail(); fetchTemplates();
    } catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  // Inline editing for phase names
  const [editingPhaseId, setEditingPhaseId] = useState<number | null>(null);
  const [phaseNameValue, setPhaseNameValue] = useState('');
  const savePhaseRename = async (phaseId: number, phase: any) => {
    if (!phaseNameValue.trim()) { setEditingPhaseId(null); return; }
    try {
      await api.updatePhase(phaseId, { name: phaseNameValue.trim(), sort_order: phase.sort_order, target_duration_days: phase.target_duration_days, description: phase.description });
      setEditingPhaseId(null); fetchDetail();
    } catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  const fetchTemplates = useCallback(async () => {
    try { setTemplates(await api.listTemplates({ scope: scope === 'all' ? undefined : scope })); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [scope]);

  useEffect(() => { setLoading(true); fetchTemplates(); }, [fetchTemplates]);

  const fetchDetail = useCallback(async () => {
    if (!selectedId) { setDetail(null); return; }
    setDetailLoading(true);
    try {
      const d = await api.getTemplate(selectedId);
      setDetail(d);
      // Auto-expand all phases
      setExpandedPhases(new Set(d.phases.map(p => p.id)));
    } catch (err) { console.error(err); }
    finally { setDetailLoading(false); }
  }, [selectedId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const isEditable = detail && detail.template.cbo_id !== null;

  // --- Template CRUD ---
  const handleCreate = async () => {
    if (!newTmpl.name.trim()) return;
    setCreating(true);
    try {
      const tmpl = await api.createTemplate(newTmpl);
      setShowCreate(false);
      setNewTmpl({ name: '', description: '', customer_type: '' });
      fetchTemplates();
      setSelectedId(tmpl.id);
    } catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
    finally { setCreating(false); }
  };

  const handleClone = async (tmpl: OnboardingTemplate) => {
    try {
      const res = await api.cloneTemplate(tmpl.id, `${tmpl.name} (Copy)`);
      fetchTemplates();
      setSelectedId(res.id);
    } catch (err: any) { alert('Error cloning: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this template and all its phases/milestones/tasks?')) return;
    try { await api.deleteTemplate(id); if (selectedId === id) setSelectedId(null); fetchTemplates(); }
    catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  // --- Phase CRUD ---
  const handleAddPhase = async () => {
    if (!phaseForm.name.trim() || !selectedId) return;
    setSaving(true);
    try {
      await api.addPhase(selectedId, {
        name: phaseForm.name,
        sort_order: detail ? detail.phases.length : 0,
        target_duration_days: phaseForm.target_duration_days ? parseInt(phaseForm.target_duration_days) : undefined,
        description: phaseForm.description || undefined,
      });
      setShowAddPhase(false);
      setPhaseForm({ name: '', target_duration_days: '', description: '' });
      fetchDetail();
    } catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
    finally { setSaving(false); }
  };

  const handleDeletePhase = async (phaseId: number) => {
    if (!confirm('Delete this phase and all its milestones/tasks?')) return;
    try { await api.deletePhase(phaseId); fetchDetail(); }
    catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  // --- Milestone CRUD ---
  const openAddMilestone = (phaseId: number) => {
    setMilestonePhaseId(phaseId);
    setMilestoneForm({ name: '', exit_criteria: '', description: '' });
    setShowAddMilestone(true);
  };

  const handleAddMilestone = async () => {
    if (!milestoneForm.name.trim() || !milestonePhaseId) return;
    setSaving(true);
    try {
      const existing = detail?.milestones.filter(m => m.phase_id === milestonePhaseId) || [];
      await api.addMilestone({
        phase_id: milestonePhaseId,
        name: milestoneForm.name,
        sort_order: existing.length,
        exit_criteria: milestoneForm.exit_criteria || undefined,
        description: milestoneForm.description || undefined,
      });
      setShowAddMilestone(false);
      fetchDetail();
    } catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
    finally { setSaving(false); }
  };

  const handleDeleteMilestone = async (id: number) => {
    if (!confirm('Delete this milestone and all its tasks?')) return;
    try { await api.deleteMilestone(id); fetchDetail(); }
    catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  // --- Task template CRUD ---
  const openAddTask = (milestoneId: number) => {
    setTaskMilestoneId(milestoneId);
    setTaskForm({ name: '', description: '', action_type: 'manual', action_target: '', default_owner_role: '', estimated_hours: '' });
    setShowAddTask(true);
  };

  const handleAddTask = async () => {
    if (!taskForm.name.trim() || !taskMilestoneId) return;
    setSaving(true);
    try {
      const existing = detail?.tasks.filter(t => t.milestone_id === taskMilestoneId) || [];
      await api.addTaskTemplate({
        milestone_id: taskMilestoneId,
        name: taskForm.name,
        sort_order: existing.length,
        description: taskForm.description || undefined,
        action_type: taskForm.action_type,
        action_target: taskForm.action_target || undefined,
        default_owner_role: taskForm.default_owner_role || undefined,
        estimated_hours: taskForm.estimated_hours ? parseFloat(taskForm.estimated_hours) : undefined,
      });
      setShowAddTask(false);
      fetchDetail();
    } catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
    finally { setSaving(false); }
  };

  const handleDeleteTask = async (id: number) => {
    try { await api.deleteTaskTemplate(id); fetchDetail(); }
    catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  // --- Build tree ---
  const phaseTree = detail ? detail.phases.map(ph => ({
    ...ph,
    milestones: detail.milestones.filter(m => m.phase_id === ph.id).map(ms => ({
      ...ms,
      tasks: detail.tasks.filter(t => t.milestone_id === ms.id),
    })),
  })) : [];

  const togglePhase = (id: number) => {
    setExpandedPhases(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">Onboarding Templates</h2>
          <p className="text-sm text-gray-500">Build and manage onboarding blueprints for customers</p>
        </div>
        <Button onClick={() => setShowCreate(true)}><Plus className="h-4 w-4 mr-1" />New Template</Button>
      </div>

      {/* Scope selector with descriptions */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {SCOPE_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setScope(s.value)}
            className={`px-3 py-1.5 rounded-md text-sm transition-colors ${scope === s.value ? 'bg-white shadow-sm font-medium text-gray-900' : 'text-gray-600 hover:text-gray-800'}`}
            title={s.desc}
          >
            {s.value === 'platform' && <Globe className="h-3 w-3 inline mr-1" />}
            {s.value === 'own' && <Building2 className="h-3 w-3 inline mr-1" />}
            {s.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-400 -mt-4">{SCOPE_OPTIONS.find(s => s.value === scope)?.desc}</p>

      <div className="grid grid-cols-12 gap-6">
        {/* Left: template list */}
        <div className="col-span-4 space-y-2">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : templates.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-8">
              {scope === 'own' ? 'No org templates yet. Clone a platform starter or create a new one.' : 'No templates found'}
            </p>
          ) : templates.map((tmpl) => (
            <div
              key={tmpl.id}
              onClick={() => setSelectedId(tmpl.id)}
              className={`p-3 border rounded-lg cursor-pointer transition-colors ${selectedId === tmpl.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  {tmpl.cbo_id === null ? <Globe className="h-3.5 w-3.5 text-blue-500 shrink-0" title="Platform starter" /> : <Building2 className="h-3.5 w-3.5 text-gray-400 shrink-0" title="Org template" />}
                  <span className="font-medium text-sm truncate">{tmpl.name}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {tmpl.is_platform_default && <Badge variant="outline" className="text-xs">Starter</Badge>}
                  <ChevronRight className="h-4 w-4 text-gray-300" />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-gray-500">{tmpl.customer_type || 'General'} · {tmpl.phase_count ?? 0} phases</span>
                {tmpl.cbo_id === null && <Badge variant="secondary" className="text-xs">Read-only — clone to edit</Badge>}
              </div>
            </div>
          ))}
        </div>

        {/* Right: template detail + editor */}
        <div className="col-span-8">
          {!selectedId ? (
            <Card><CardContent className="py-12 text-center text-gray-400">Select a template to view and edit its structure</CardContent></Card>
          ) : detailLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
          ) : !detail ? (
            <Card><CardContent className="py-12 text-center text-gray-400">Template not found</CardContent></Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    {editingTmplName ? (
                      <div className="flex items-center gap-1">
                        <Input className="h-8 text-lg font-semibold w-72" value={tmplNameValue} onChange={(e) => setTmplNameValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && saveTmplName()} autoFocus />
                        <Button size="sm" variant="ghost" onClick={saveTmplName}><Save className="h-3 w-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingTmplName(false)}><X className="h-3 w-3" /></Button>
                      </div>
                    ) : (
                      <CardTitle className={isEditable ? 'cursor-pointer hover:text-blue-600' : ''} onClick={() => { if (isEditable) { setTmplNameValue(detail.template.name); setEditingTmplName(true); } }}>
                        {detail.template.name}{isEditable && <Edit className="h-3 w-3 inline ml-1.5 opacity-40" />}
                      </CardTitle>
                    )}
                    {detail.template.description && <p className="text-sm text-gray-500 mt-1">{detail.template.description}</p>}
                    {!isEditable && (
                      <p className="text-xs text-amber-600 mt-1 bg-amber-50 px-2 py-1 rounded inline-block">
                        Platform starter — clone to your org library to edit
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleClone(detail.template)}>
                      <Copy className="h-3 w-3 mr-1" />Clone
                    </Button>
                    {isEditable && (
                      <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(detail.template.id)}>
                        <Trash2 className="h-3 w-3 mr-1" />Delete
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Phase tree */}
                {phaseTree.length === 0 && !isEditable ? (
                  <p className="text-gray-400 text-center py-6">No phases defined</p>
                ) : (
                  <div className="space-y-3">
                    {phaseTree.map((phase, pi) => (
                      <div key={phase.id} className="border rounded-lg overflow-hidden">
                        {/* Phase header */}
                        <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1">
                            <button onClick={() => togglePhase(phase.id)} className="flex items-center gap-1">
                              {expandedPhases.has(phase.id) ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                              <span className="text-xs font-mono text-gray-400">P{pi + 1}</span>
                            </button>
                            {editingPhaseId === phase.id ? (
                              <div className="flex items-center gap-1">
                                <Input className="h-6 text-sm font-medium w-48" value={phaseNameValue} onChange={(e) => setPhaseNameValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && savePhaseRename(phase.id, phase)} autoFocus />
                                <Button size="sm" variant="ghost" className="h-5 px-1" onClick={() => savePhaseRename(phase.id, phase)}><Save className="h-3 w-3" /></Button>
                                <Button size="sm" variant="ghost" className="h-5 px-1" onClick={() => setEditingPhaseId(null)}><X className="h-3 w-3" /></Button>
                              </div>
                            ) : (
                              <button onClick={() => togglePhase(phase.id)} className="flex items-center gap-1" onDoubleClick={(e) => { if (isEditable) { e.stopPropagation(); setEditingPhaseId(phase.id); setPhaseNameValue(phase.name); } }}>
                                <span className="font-medium text-sm">{phase.name}</span>
                                {phase.target_duration_days && <span className="text-xs text-gray-400 ml-1">({phase.target_duration_days}d)</span>}
                              </button>
                            )}
                          </div>
                          {isEditable && (
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => openAddMilestone(phase.id)} title="Add milestone"><Plus className="h-3 w-3" /></Button>
                              <Button size="sm" variant="ghost" className="h-6 px-1 text-red-500" onClick={() => handleDeletePhase(phase.id)} title="Delete phase"><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          )}
                        </div>

                        {expandedPhases.has(phase.id) && (
                          <div className="divide-y">
                            {phase.milestones.length === 0 && (
                              <div className="px-4 py-3 text-sm text-gray-400 text-center">
                                No milestones.{isEditable && <button className="text-blue-500 hover:underline ml-1" onClick={() => openAddMilestone(phase.id)}>Add one</button>}
                              </div>
                            )}
                            {phase.milestones.map((ms) => (
                              <div key={ms.id} className="px-4 py-2">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm font-medium text-gray-700">{ms.name}</p>
                                    {ms.exit_criteria && <p className="text-xs text-gray-400 mt-0.5">Exit: {ms.exit_criteria}</p>}
                                  </div>
                                  {isEditable && (
                                    <div className="flex gap-1">
                                      <Button size="sm" variant="ghost" className="h-5 px-1" onClick={() => openAddTask(ms.id)} title="Add task"><Plus className="h-3 w-3" /></Button>
                                      <Button size="sm" variant="ghost" className="h-5 px-1 text-red-400" onClick={() => handleDeleteMilestone(ms.id)} title="Delete milestone"><Trash2 className="h-2.5 w-2.5" /></Button>
                                    </div>
                                  )}
                                </div>
                                {ms.tasks.length > 0 && (
                                  <div className="mt-2 space-y-1 ml-3">
                                    {ms.tasks.map((task) => (
                                      <div key={task.id} className="flex items-center gap-2 text-sm group">
                                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0" />
                                        <span className="text-gray-600 flex-1">{task.name}</span>
                                        <Badge variant="outline" className="text-xs">{task.action_type}</Badge>
                                        {task.default_owner_role && <Badge variant="secondary" className="text-xs">{task.default_owner_role}</Badge>}
                                        {task.estimated_hours && <span className="text-xs text-gray-400">{task.estimated_hours}h</span>}
                                        {isEditable && (
                                          <Button size="sm" variant="ghost" className="h-5 px-1 opacity-0 group-hover:opacity-100 text-red-400" onClick={() => handleDeleteTask(task.id)}>
                                            <X className="h-3 w-3" />
                                          </Button>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {ms.tasks.length === 0 && (
                                  <p className="text-xs text-gray-300 mt-1 ml-3">
                                    No tasks.{isEditable && <button className="text-blue-500 hover:underline ml-1" onClick={() => openAddTask(ms.id)}>Add one</button>}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Add phase button */}
                    {isEditable && (
                      <Button variant="outline" className="w-full border-dashed" onClick={() => { setPhaseForm({ name: '', target_duration_days: '', description: '' }); setShowAddPhase(true); }}>
                        <Plus className="h-4 w-4 mr-1" />Add Phase
                      </Button>
                    )}
                  </div>
                )}

                {phaseTree.length === 0 && isEditable && (
                  <div className="text-center py-6">
                    <p className="text-gray-400 mb-2">No phases yet. Start building your template.</p>
                    <Button variant="outline" onClick={() => { setPhaseForm({ name: '', target_duration_days: '', description: '' }); setShowAddPhase(true); }}>
                      <Plus className="h-4 w-4 mr-1" />Add First Phase
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* ===== Create template dialog ===== */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Onboarding Template</DialogTitle>
            <DialogDescription>Create a blank template for your organization, then add phases, milestones, and tasks.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Name</Label><Input value={newTmpl.name} onChange={(e) => setNewTmpl({ ...newTmpl, name: e.target.value })} className="mt-1" placeholder="e.g., CBO CalAIM Onboarding" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={newTmpl.description} onChange={(e) => setNewTmpl({ ...newTmpl, description: e.target.value })} className="mt-1" /></div>
            <div><Label>Customer Type</Label><Input value={newTmpl.customer_type} onChange={(e) => setNewTmpl({ ...newTmpl, customer_type: e.target.value })} className="mt-1" placeholder="e.g., cbo, sponsor, employer, payer" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating || !newTmpl.name.trim()}>
              {creating ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Creating...</> : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Add phase dialog ===== */}
      <Dialog open={showAddPhase} onOpenChange={setShowAddPhase}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Phase</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Phase Name</Label><Input value={phaseForm.name} onChange={(e) => setPhaseForm({ ...phaseForm, name: e.target.value })} className="mt-1" placeholder="e.g., Kickoff, Configuration, Data Migration" /></div>
            <div><Label>Target Duration (days)</Label><Input type="number" value={phaseForm.target_duration_days} onChange={(e) => setPhaseForm({ ...phaseForm, target_duration_days: e.target.value })} className="mt-1 w-32" placeholder="e.g., 14" /></div>
            <div><Label>Description (optional)</Label><Textarea rows={2} value={phaseForm.description} onChange={(e) => setPhaseForm({ ...phaseForm, description: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPhase(false)}>Cancel</Button>
            <Button onClick={handleAddPhase} disabled={saving || !phaseForm.name.trim()}>{saving ? 'Saving...' : 'Add Phase'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Add milestone dialog ===== */}
      <Dialog open={showAddMilestone} onOpenChange={setShowAddMilestone}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Milestone</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Milestone Name</Label><Input value={milestoneForm.name} onChange={(e) => setMilestoneForm({ ...milestoneForm, name: e.target.value })} className="mt-1" placeholder="e.g., Organization & Branding" /></div>
            <div><Label>Exit Criteria (optional)</Label><Input value={milestoneForm.exit_criteria} onChange={(e) => setMilestoneForm({ ...milestoneForm, exit_criteria: e.target.value })} className="mt-1" placeholder="What must be true to exit this milestone?" /></div>
            <div><Label>Description (optional)</Label><Textarea rows={2} value={milestoneForm.description} onChange={(e) => setMilestoneForm({ ...milestoneForm, description: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMilestone(false)}>Cancel</Button>
            <Button onClick={handleAddMilestone} disabled={saving || !milestoneForm.name.trim()}>{saving ? 'Saving...' : 'Add Milestone'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Add task dialog ===== */}
      <Dialog open={showAddTask} onOpenChange={setShowAddTask}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Task</DialogTitle>
            <DialogDescription>Define a task that will be materialized when a plan uses this template.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Task Name</Label><Input value={taskForm.name} onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })} className="mt-1" placeholder="e.g., Configure SAML/SSO" /></div>
            <div><Label>Description (optional)</Label><Textarea rows={2} value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Action Type</Label>
                <Select value={taskForm.action_type} onValueChange={(v) => setTaskForm({ ...taskForm, action_type: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACTION_TYPES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Default Owner Role</Label>
                <Select value={taskForm.default_owner_role} onValueChange={(v) => setTaskForm({ ...taskForm, default_owner_role: v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select role..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {DEFAULT_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(taskForm.action_type === 'link' || taskForm.action_type === 'wizard') && (
              <div>
                <Label>Action Target (route or component)</Label>
                <Input value={taskForm.action_target} onChange={(e) => setTaskForm({ ...taskForm, action_target: e.target.value })} className="mt-1" placeholder="e.g., /admin/organization-settings?tab=branding" />
              </div>
            )}
            <div>
              <Label>Estimated Hours</Label>
              <Input type="number" step="0.5" value={taskForm.estimated_hours} onChange={(e) => setTaskForm({ ...taskForm, estimated_hours: e.target.value })} className="mt-1 w-32" placeholder="e.g., 2" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTask(false)}>Cancel</Button>
            <Button onClick={handleAddTask} disabled={saving || !taskForm.name.trim()}>{saving ? 'Saving...' : 'Add Task'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
